import { Theme, ButtonProps as MuiButtonProps, alpha, Palette } from '@mui/material';
import { globalClasses } from 'core/common/constants/global-classes';
import { AnswerButtonVariant } from 'core/flow/entities';
import { pointerHoverMediaQuery, SeasonSansFontFamily } from '../constants';

// ts-prune-ignore-next-line
export enum ButtonAnimation {
  PULSE = 'pulse',
}
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    option: true;
    image: true;
  }

  interface ButtonPropsColorOverrides {
    paywall: true;
  }

  interface CustomButtonProps extends MuiButtonProps {
    gutterBottom?: boolean;
    animation?: ButtonAnimation;
    m?: number | string;
    mt?: number | string;
    mb?: number | string;
    ml?: number | string;
    mr?: number | string;
    mx?: number | string;
    my?: number | string;
  }
}

const getPulseRingColor = (palette: Palette, color?: MuiButtonProps['color']) =>
  color === 'paywall' ? palette.paywall.cta : palette.button.main;

const getButtonAnimation = (
  palette: Palette,
  animation?: ButtonAnimation,
  color?: MuiButtonProps['color'],
) => {
  if (animation === ButtonAnimation.PULSE) {
    return {
      position: 'relative',
      animation: `buttonBreathe 3s ease-in-out infinite`,
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: -1,
        borderRadius: '50px',
        border: `1.5px solid ${getPulseRingColor(palette, color)}`,
        pointerEvents: 'none',
        animation: `buttonPulse 3s linear infinite`,
      },
      '&::after': {
        animationDelay: '-1.5s',
      },
      '@keyframes buttonBreathe': {
        '0%, 100%': {
          transform: 'scale(1)',
        },
        '50%': {
          transform: 'scale(1.03)',
        },
      },
      '@keyframes buttonPulse': {
        '0%': {
          inset: '0px',
          opacity: 0,
        },
        '15%': {
          opacity: 0.7,
        },
        '100%': {
          inset: '-16px',
          opacity: 0,
        },
      },
    };
  }
  return {};
};

export const getButtonOptions = ({ palette, breakpoints }: Theme) => {
  const buttonComponents = {
    styleOverrides: {
      root: ({
        ownerState,
      }: {
        ownerState: {
          gutterBottom?: boolean;
          animation?: ButtonAnimation;
          variant: AnswerButtonVariant;
          color?: MuiButtonProps['color'];
        };
      }) => {
        const baseStyles = {
          fontFamily: SeasonSansFontFamily,
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          textTransform: 'none',
          display: 'flex',
          fontWeight: 400,
          lineHeight: '150%',
        };

        const imageVariantStyles = {
          overflow: ownerState.variant === 'image' && 'hidden',
        };

        return {
          ...baseStyles,
          ...imageVariantStyles,
          ...getButtonAnimation(palette, ownerState.animation, ownerState.color),
        };
      },
      sizeSmall: {
        padding: '4px 12px',
        fontSize: '12px',
        height: '32px',
        [breakpoints.up('mobile')]: {
          fontSize: '14px',
        },
      },
      sizeMedium: {
        padding: '8px 24px',
        fontSize: '16px',
        height: '40px',
      },
      sizeLarge: {
        padding: '8px 24px',
        fontSize: '16px',
        height: '48px',
        [breakpoints.up('mobile')]: {
          fontSize: '18px',
        },
      },
      option: {
        [`& ${globalClasses.buttonStartIcon}`]: {
          marginLeft: 0,
        },
      },
    },
    defaultProps: {
      disableElevation: true,
      size: 'large',
    },
    variants: [
      {
        props: { variant: 'text' },
        style: {
          color: palette.button.contrastTextButtonText,
          borderRadius: '30px',
          fontWeight: 500,
        },
      },
      {
        props: { variant: 'contained' },
        style: {
          backgroundColor: palette.button.main,
          color: palette.button.contrastMainText,
          borderRadius: '30px',
          fontWeight: 500,
          [pointerHoverMediaQuery]: {
            '&:hover': {
              backgroundColor: palette.button.mainHover,
              color: palette.button.contrastMainText,
            },
          },
          '&:disabled': {
            backgroundColor: palette.button.mainDisabled,
            color: palette.button.contrastMainText,
          },
        },
      },
      {
        props: { variant: 'contained', color: 'secondary' },
        style: {
          backgroundColor: palette.button.secondary,
          color: palette.button.contrastSecondaryText,
          borderRadius: '30px',
          fontWeight: 500,
          [pointerHoverMediaQuery]: {
            '&:hover': {
              backgroundColor: palette.button.secondaryHover,
              color: palette.button.contrastSecondaryText,
            },
          },
          '&:disabled': {
            backgroundColor: palette.button.secondaryDisabled,
            color: palette.button.contrastSecondaryText,
          },
        },
      },
      {
        props: { variant: 'contained', color: 'paywall' },
        style: {
          backgroundColor: palette.paywall.cta,
          color: palette.paywall.onCta,
          borderRadius: '30px',
          fontWeight: 500,
          [pointerHoverMediaQuery]: {
            '&:hover': {
              backgroundColor: palette.paywall.ctaHover,
              color: palette.paywall.onCta,
            },
          },
          '&:disabled': {
            backgroundColor: palette.paywall.ctaDisabled,
            color: palette.paywall.onCta,
          },
        },
      },
      {
        props: { variant: 'option' },
        style: {
          backgroundColor: palette.button.option,
          border: `1px solid ${palette.button.optionBorder}`,
          color: palette.button.contrastOptionText,
          borderRadius: '8px',
          justifyContent: 'flex-start',
          minHeight: '56px',
          padding: '16px',
          letterSpacing: '0px',
          '& .MuiButton-icon': {
            marginInlineEnd: '12px',
          },
          '& svg': {
            color: palette.button.optionIcon,
          },
          [pointerHoverMediaQuery]: {
            '&:hover': {
              border: `1px solid ${palette.button.optionHover}`,
              color: palette.button.contrastOptionText,
              backgroundColor: alpha(palette.button.optionHover, 0.2),
            },
          },
          '&:disabled': {
            backgroundColor: palette.button.optionDisabled,
            color: palette.button.contrastOptionText,
          },
        },
      },
      {
        props: { variant: 'image' },
        style: {
          backgroundColor: palette.button.image,
          border: '1px solid transparent',
          backgroundClip: 'padding-box',
          color: palette.button.contrastImageText,
          borderRadius: '12px',
          height: '96px',
          justifyContent: 'flex-start',
          [pointerHoverMediaQuery]: {
            '&:hover': {
              border: `1px solid ${palette.button.imageHover}`,
              color: palette.button.contrastImageText,
              backgroundColor: palette.button.image,
            },
          },
          '&:disabled': {
            backgroundColor: palette.button.imageDisabled,
            color: palette.button.contrastImageText,
          },
        },
      },
    ],
  };

  return { buttonComponents };
};
